import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import CheckmarkCurvedIcon from "@src/lib/icons/12px/checkmark-curved";
import ChevronRightIcon from "@src/lib/icons/12px/chevron-right";
import DotFilledIcon from "@src/lib/icons/12px/dot-filled";

import { cn } from "@src/lib/utils/cn";
import { MENU_MOTION_CLASSNAME } from "@src/lib/ui/menuMotion";
import {
  MENU_ITEM_CLASSNAME,
  MENU_ITEM_DESTRUCTIVE_CLASSNAME,
  MENU_ITEM_INDICATOR_CLASSNAME,
  MENU_ITEM_INDICATOR_INSET_CLASSNAME,
  MENU_LABEL_CLASSNAME,
  MENU_RADIO_DOT_CLASSNAME,
  MENU_SEPARATOR_CLASSNAME,
  MENU_SHORTCUT_CLASSNAME,
  MENU_SUB_TRIGGER_CHEVRON_CLASSNAME,
  MENU_SUB_TRIGGER_CLASSNAME,
  MENU_SURFACE_CLASSNAME,
} from "@src/lib/ui/menuStyles";

const DROPDOWN_MENU_ORIGIN_CLASSNAME =
  "origin-[var(--radix-dropdown-menu-content-transform-origin)]";

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      MENU_ITEM_CLASSNAME,
      MENU_SUB_TRIGGER_CLASSNAME,
      inset && "pl-8",
      className,
    )}
    {...props}
  >
    {children}
    <ChevronRightIcon className={MENU_SUB_TRIGGER_CHEVRON_CLASSNAME} />
  </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  // Portalled: the parent menu's motion sets will-change: transform, which
  // makes it the containing block for the sub-menu's fixed positioning, so
  // an in-place sub-menu is clipped by the parent's overflow-hidden.
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      className={cn(
        "z-[9999]",
        MENU_SURFACE_CLASSNAME,
        DROPDOWN_MENU_ORIGIN_CLASSNAME,
        MENU_MOTION_CLASSNAME,
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-[9999]",
        MENU_SURFACE_CLASSNAME,
        DROPDOWN_MENU_ORIGIN_CLASSNAME,
        MENU_MOTION_CLASSNAME,
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
    dataTestId?: string;
    /** "destructive" paints the row in the error text color (Delete, Remove). */
    variant?: "default" | "destructive";
  }
>(({ className, inset, dataTestId, variant = "default", ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    data-testid={dataTestId}
    className={cn(
      MENU_ITEM_CLASSNAME,
      variant === "destructive" && MENU_ITEM_DESTRUCTIVE_CLASSNAME,
      inset && "pl-8",
      className,
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      MENU_ITEM_CLASSNAME,
      MENU_ITEM_INDICATOR_INSET_CLASSNAME,
      className,
    )}
    checked={checked}
    {...props}
  >
    <span className={MENU_ITEM_INDICATOR_CLASSNAME}>
      <DropdownMenuPrimitive.ItemIndicator>
        <CheckmarkCurvedIcon />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      MENU_ITEM_CLASSNAME,
      MENU_ITEM_INDICATOR_INSET_CLASSNAME,
      className,
    )}
    {...props}
  >
    <span className={MENU_ITEM_INDICATOR_CLASSNAME}>
      <DropdownMenuPrimitive.ItemIndicator>
        <DotFilledIcon className={MENU_RADIO_DOT_CLASSNAME} />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(MENU_LABEL_CLASSNAME, inset && "pl-8", className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn(MENU_SEPARATOR_CLASSNAME, className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>): React.ReactElement => {
  return <span className={cn(MENU_SHORTCUT_CLASSNAME, className)} {...props} />;
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
};
